const WINDOW_NAME_KEY = '__bpmLegiaoSession__';

export const getStorageTargets = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storages = [];

  const candidates = ['localStorage', 'sessionStorage'];
  candidates.forEach((key) => {
    try {
      const storage = window[key];
      if (storage && typeof storage.getItem === 'function') {
        storages.push(storage);
      }
    } catch (error) {
      // Ignore storage engines that are not available in the current context
    }
  });

  return storages;
};

const parseWindowNamePayload = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.name;
  if (!raw || typeof raw !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (error) {
    // Ignore malformed values so that legacy window.name usages do not break the flow
  }

  return null;
};

const writeWindowNamePayload = (payload) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (!payload || typeof payload !== 'object' || Object.keys(payload).length === 0) {
      window.name = '';
      return;
    }

    window.name = JSON.stringify(payload);
  } catch (error) {
    // If writing fails we silently drop the value so the app can continue working
  }
};

export const readWindowNameSession = () => {
  const payload = parseWindowNamePayload();
  if (!payload || !Object.prototype.hasOwnProperty.call(payload, WINDOW_NAME_KEY)) {
    return null;
  }

  const session = payload[WINDOW_NAME_KEY];
  if (!session || typeof session !== 'object') {
    return null;
  }

  return {
    token: session.token || '',
    user: session.user || null,
  };
};

export const writeWindowNameSession = (session) => {
  if (!session || typeof session !== 'object') {
    clearWindowNameSession();
    return;
  }

  const payload = parseWindowNamePayload() || {};
  payload[WINDOW_NAME_KEY] = {
    token: session.token || '',
    user: session.user || null,
    updatedAt: Date.now(),
  };
  writeWindowNamePayload(payload);
};

export const clearWindowNameSession = () => {
  const payload = parseWindowNamePayload();
  if (!payload) {
    writeWindowNamePayload({});
    return;
  }

  if (Object.prototype.hasOwnProperty.call(payload, WINDOW_NAME_KEY)) {
    delete payload[WINDOW_NAME_KEY];
  }

  writeWindowNamePayload(payload);
};

export const readStorageValue = (key) => {
  if (!key) return null;

  const storages = getStorageTargets();
  for (const storage of storages) {
    try {
      const value = storage.getItem(key);
      if (value !== null && value !== undefined) {
        return value;
      }
    } catch (error) {
      // Ignore read errors from this storage and continue with the next one
    }
  }

  return null;
};

export const writeStorageValue = (key, value) => {
  if (!key) return;

  const stringValue = value == null ? null : String(value);
  const storages = getStorageTargets();

  storages.forEach((storage) => {
    try {
      if (stringValue === null) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, stringValue);
      }
    } catch (error) {
      // Ignore write errors so that unavailable storages do not break the flow
    }
  });
};

export const removeStorageValue = (key) => {
  if (!key) return;

  const storages = getStorageTargets();
  storages.forEach((storage) => {
    try {
      storage.removeItem(key);
    } catch (error) {
      // Ignore removal errors and continue with the next storage engine
    }
  });
};

export const readStorageJSON = (key) => {
  const raw = readStorageValue(key);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const writeStorageJSON = (key, value) => {
  if (value == null) {
    removeStorageValue(key);
    return;
  }

  try {
    writeStorageValue(key, JSON.stringify(value));
  } catch (error) {
    removeStorageValue(key);
  }
};
